import { useEffect } from 'react';

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useQueryClient } from '@tanstack/react-query';

import { getHostName, TOKEN_KEY_NAME } from '@app/services/httpClient';

interface IStockChangedPayload {
  warehouse_uid: string;
  product_uids: string[];
}

/**
 * SSE-подписка на маяки об изменениях остатков конкретного склада.
 * Сервер маршрутизирует сообщения по scope текущего юзера (берётся из JWT)
 * и переданному `warehouseUid`. Если он не задан — соединение не открывается.
 *
 * При получении event=stock инвалидирует кэши каталога и корзины, чтобы
 * соответствующие хуки выполнили рефетч с применением лимитов под юзера.
 */
export const useStockUpdatesSubscription = (warehouseUid: string | undefined) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!warehouseUid) return;

    const ctrl = new AbortController();
    const token = localStorage.getItem(TOKEN_KEY_NAME);
    if (!token) return;

    const url = `${getHostName()}/products/realtime/stock-updates?warehouse_uid=${encodeURIComponent(warehouseUid)}`;

    fetchEventSource(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
      // не держим соединение, когда вкладка не активна
      openWhenHidden: false,
      onmessage: (msg) => {
        if (msg.event !== 'stock' || !msg.data) return;

        try {
          const payload = JSON.parse(msg.data) as IStockChangedPayload;
          // payload пока используем только как сигнал «что-то изменилось».
          // Гранулярная инвалидация по product_uids — следующий шаг, если понадобится.
          void payload;
        } catch {
          return;
        }

        queryClient.invalidateQueries({ queryKey: ['getCatalogProducts'] });
        queryClient.invalidateQueries({ queryKey: ['getCart'] });
      },
      onerror: (err) => {
        // fetch-event-source сам ретраит с backoff. Логируем, не пробрасываем,
        // чтобы библиотека продолжила переподключение.
        // eslint-disable-next-line no-console
        console.warn('SSE stock-updates error', err);
      },
    }).catch(() => {
      // отписались через AbortController — это штатное завершение
    });

    return () => ctrl.abort();
  }, [warehouseUid, queryClient]);
};
